import { useMemo, useState } from 'react';
import { trpc } from '@/lib/trpc';
import { ArticleCard } from '@/components/article/ArticleCard';
import { ArticleFormModal } from '@/components/article/ArticleFormModal';
import { CustomButton } from '@/components/uiWrappers/button';
import { Input } from '@/components/ui/input';
import { IoAddOutline } from 'react-icons/io5';
import { ArticleAssignmentBucket } from '@mightybyte/business';

type ViewFilter = 'general' | 'buckets';

export function ArticlesScreen() {
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [moduleFilter, setModuleFilter] = useState('');
  const [viewFilter, setViewFilter] = useState<ViewFilter>('general');

  const filterNumber = moduleFilter.trim() === ''
    ? undefined
    : parseInt(moduleFilter, 10) || undefined;

  const { data, isLoading } = trpc.admin.article.getArticles.useQuery({
    moduleNumber: filterNumber,
  });

  const articles = useMemo(() => {
    let filtered = data?.articles ?? [];

    // Filter by view type
    if (viewFilter === 'general') {
      // Only show articles that have at least one General assignment
      filtered = filtered.filter(article =>
        article.moduleAssignments.some(assignment => assignment.bucket === ArticleAssignmentBucket.General)
      );
    } else {
      // 'buckets' - show articles that have Bucket 1 or Bucket 2 assignments
      filtered = filtered.filter(article =>
        article.moduleAssignments.some(assignment =>
          assignment.bucket === ArticleAssignmentBucket.One ||
          assignment.bucket === ArticleAssignmentBucket.Two
        )
      );
    }

    // Sort by module number, then by bucket (for bucket view), then by order
    return filtered.sort((a, b) => {
      // Get the first (or lowest) module assignment for each article
      const aAssignment = a.moduleAssignments[0];
      const bAssignment = b.moduleAssignments[0];

      if (!aAssignment && !bAssignment) return 0;
      if (!aAssignment) return 1;
      if (!bAssignment) return -1;

      // First sort by module number
      if (aAssignment.moduleNumber !== bAssignment.moduleNumber) {
        return aAssignment.moduleNumber - bAssignment.moduleNumber;
      }

      // For buckets view, sort by bucket type (One before Two)
      if (viewFilter === 'buckets') {
        const bucketOrder = {
          [ArticleAssignmentBucket.One]: 1,
          [ArticleAssignmentBucket.Two]: 2,
          [ArticleAssignmentBucket.General]: 3,
        };
        const aBucketOrder = bucketOrder[aAssignment.bucket];
        const bBucketOrder = bucketOrder[bAssignment.bucket];

        if (aBucketOrder !== bBucketOrder) {
          return aBucketOrder - bBucketOrder;
        }
      }

      // Then sort by order within the module/bucket
      const aOrder = aAssignment.order ?? 999999;
      const bOrder = bAssignment.order ?? 999999;
      return aOrder - bOrder;
    });
  }, [data?.articles, viewFilter]);

  return (
    <div className="flex-1">
      <ArticleFormModal
        isOpen={isNewModalOpen}
        onClose={() => setIsNewModalOpen(false)}
      />

      {/* Header */}
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          {/* View toggle buttons */}
          <div className="inline-flex rounded-lg border border-gray-300 p-1 bg-gray-50">
            <button
              onClick={() => setViewFilter('general')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                viewFilter === 'general'
                  ? 'bg-brand text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              General
            </button>
            <button
              onClick={() => setViewFilter('buckets')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                viewFilter === 'buckets'
                  ?  'bg-brand text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Buckets
            </button>
          </div>

          {/* Module filter */}
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min="1"
              value={moduleFilter}
              onChange={(e) => setModuleFilter(e.target.value)}
              placeholder="Filter by module #"
              className="w-44"
            />
            {moduleFilter && (
              <button
                onClick={() => setModuleFilter('')}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        <CustomButton variant="default" onClick={() => setIsNewModalOpen(true)}>
          <IoAddOutline size={28} data-icon="inline-start" />
          New Article
        </CustomButton>
      </div>

      {/* Content */}
      <div className="mt-6">
        {isLoading ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-500">Loading articles...</p>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-6">
              {articles.map((article) => (
                <ArticleCard key={article._id} article={article} />
              ))}
            </div>

            {articles.length === 0 && (
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {filterNumber ? `No articles for module ${filterNumber}` : 'No Articles Yet'}
                </h3>
                {!filterNumber && (
                  <p className="text-gray-500 mb-4">
                    Get started by creating your first article.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
